import React from "react";
import { Button, Grid, Image } from "semantic-ui-react";
import { useSelector } from "react-redux";
import Tags from "../../../app/common/components/Tags";
import linkedinIcon from "../../../assets/icons/iconmonstr-linkedin-3.png";
import facebookIcon from "../../../assets/icons/facebook-icon.png";
import twitterIcon from "../../../assets/icons/twitter-icon.png";
import instaIcon from "../../../assets/icons/insta-icon.png";
import {
  ContactDetailsAddressIcon,
  ContactDetailsEmailIcon,
  ContactDetailsNameIcon,
  ContactDetailsPhoneIcon,
} from "../../../assets/icons";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { useHistory } from "react-router-dom";

export default function CompanyProfilePreview({ company, clearAbove = true }) {
  const history = useHistory();

  const { currentUserAccount } = useSelector((state) => state.account);
  const { responsive } = useSelector((state) => state.app);

  if (!company) return <LoadingComponent initialLoading={true} />;

  return (
    <Grid className="body-blue-text" stackable>
      <Grid.Row>
        {!company.isPaidFor && (
          <Grid.Column computer={8} tablet={13} mobile={15}>
            <div className="dashboard-message body-blue-text">
              <div className="semi-bold">Awaiting payment</div>
              Your profile will appear under review when payment is received
            </div>
          </Grid.Column>
        )}
        {company.isPaidFor && !company.isApproved && (
          <Grid.Column computer={8} tablet={13} mobile={15}>
            <div className="dashboard-message body-blue-text">
              <div className="semi-bold">Profile is under review</div>
              We’ll send you an email notification when it’s published.
            </div>
          </Grid.Column>
        )}
        <Grid.Column width={8}>
          <Button
            className={"overwrite-button teal-inverse-button"}
            content={"View public profile"}
            disabled={!company.isApproved}
            style={{ marginTop: responsive.isMobile ? "10px" : 0 }}
            onClick={() =>
              currentUserAccount.isEmployer
                ? history.push(`/companies/${company.refName}`)
                : window.open(`/companies/${company.refName}`, "_blank")
            }
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className={clearAbove ? "clear-above" : ""}>
        <Grid.Column>
          <h1>{company.name}</h1>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={8}>
          <h2>Quick Facts</h2>
          <div className="details-item">
            <span className="body-heavy-text">Company type: </span>
            <span>{company.type}</span>
          </div>
          <div className="details-item">
            <span className="body-heavy-text">Industry: </span>
            <span>{company.industry}</span>
          </div>
          <div className="details-item">
            <span className="body-heavy-text">Number of employees:{""}</span>
            <span>{company.employeeSize}</span>
          </div>
          <div className="details-item">
            <span className="body-heavy-text">Year founded: </span>
            <span>{company.yearFounded}</span>
          </div>
          <div className="details-item">
            <span className="body-heavy-text">Awards: </span>
            <span>{company.awards.join(", ")}</span>
          </div>
        </Grid.Column>
        <Grid.Column computer={4} tablet={4} mobile={2}>
          <div
            className="company-logo-image"
            style={{ width: responsive.isMobile ? "50%" : "90%" }}
          >
            <Image src={company.companyLogoPhotoURL} />
          </div>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={11} style={{ padding: "17px 14px" }}>
          <h2>About Us</h2>
          {company.cultureTags.length > 0 && (
            <Tags tags={company.cultureTags} style={{ margin: "0 0 10px 0" }} />
          )}
          <div
            className="rich-text-display-block"
            dangerouslySetInnerHTML={{
              __html: company.description,
            }}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <h2>Website & Social Links</h2>
          <div className="details-item" style={{ marginBottom: 10 }}>
            <span className="body-heavy-text">Website: </span>
            <span>
              <a
                className="text-link"
                href={company.websiteLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {company.websiteLink}
              </a>
            </span>
          </div>
          <div className="details-item display-flex">
            <span style={{ width: 40, marginRight: 10 }}>
              <img src={linkedinIcon} alt="social icon" />
            </span>
            <span style={{ paddingTop: 3 }}>
              <a
                className="text-link"
                href={company.linkedInLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {company.linkedInLink}
              </a>
            </span>
          </div>
          <div className="details-item display-flex">
            <span style={{ width: 40, marginRight: 10 }}>
              <img src={twitterIcon} alt="social icon" />
            </span>
            <span style={{ paddingTop: 3 }}>
              <a
                className="text-link"
                href={company.twitterLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {company.twitterLink}
              </a>
            </span>
          </div>
          <div className="details-item display-flex">
            <span style={{ width: 40, marginRight: 10 }}>
              <img src={facebookIcon} alt="social icon" />
            </span>
            <span style={{ paddingTop: 3 }}>
              <a
                className="text-link"
                href={company.facebookLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {company.facebookLink}
              </a>
            </span>
          </div>
          <div className="details-item display-flex">
            <span style={{ width: 40, marginRight: 10 }}>
              <img src={instaIcon} alt="social icon" />
            </span>
            <span style={{ paddingTop: 3 }}>
              <a
                className="text-link"
                href={company.instagramLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {company.instagramLink}
              </a>
            </span>
          </div>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={7}>
          <h2>
            Main Company Address{" "}
            <span className="body-blue-text"> (shown on map)</span>
          </h2>
          {company.location.address}
        </Grid.Column>
        <Grid.Column width={6}>
          <h2>Contact Information</h2>
          <div className="details-item">
            <ContactDetailsNameIcon />
            <span>{company.contactName}</span>
          </div>
          <div className="details-item">
            <ContactDetailsEmailIcon />
            <span>{company.contactEmail}</span>
          </div>
          <div className="details-item">
            <ContactDetailsPhoneIcon />
            <span>{company.phoneNumber}</span>
          </div>
          <div className="details-item">
            <ContactDetailsAddressIcon />
            <span>{company.location.address}</span>
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
}
